import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Due } from "../api/types";
import { useT } from "../i18n";
import { colorHex, PRIORITY_COLORS } from "../lib/colors";
import { dueLabel } from "../lib/dates";
import { highlightSegments, parseForHighlight, quickAddContext } from "../lib/quickadd";
import { useApp, useSyncState } from "../store";
import { type QuickDefaults, quickAddCommands } from "../sync/quickSubmit";
import { IconCalendar, IconChevronDown, IconFlag, IconHash, IconInbox, IconLabel } from "../ui/icons";
import {
  DatePicker,
  type DueChoice,
  LabelPicker,
  PriorityMenu,
  ProjectPicker,
  type ProjectTarget,
} from "../ui/pickers";
import { usePrefs } from "../ui/prefs";
import { useAnchor } from "../ui/primitives";

/** Textarea with a mirrored layer that highlights Quick Add tokens as you type. */
export function HighlightInput({
  value,
  onChange,
  placeholder,
  onEnter,
  className,
  autoFocus,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  onEnter?: () => void;
  className?: string;
  autoFocus?: boolean;
  testId?: string;
}) {
  const state = useSyncState();
  const ref = useRef<HTMLTextAreaElement>(null);
  const ctx = useMemo(() => quickAddContext(state), [state]);
  const segments = useMemo(() => highlightSegments(value, parseForHighlight(value, ctx)), [value, ctx]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${el.scrollHeight}px`;
  });
  return (
    <div className={`hl ${className ?? ""}`}>
      <div className="hl-mirror" aria-hidden="true">
        {segments.map((s) =>
          s.type ? (
            <mark
              key={s.start}
              className={`tok tok-${s.type}${s.priority ? ` tok-priority-${s.priority}` : ""}`}
              data-token={s.type}
              style={{ color: "transparent" }}
            >
              {s.text}
            </mark>
          ) : (
            <span key={s.start}>{s.text}</span>
          ),
        )}
        {"​"}
      </div>
      <textarea
        ref={ref}
        className="hl-input"
        rows={1}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value.replace(/\n/g, " "))}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.nativeEvent.isComposing) {
            e.preventDefault();
            onEnter?.();
          }
        }}
      />
    </div>
  );
}

export function TaskEditor({
  defaults,
  onClose,
  onSubmitted,
  variant = "inline",
  closeOnSubmit = false,
}: {
  defaults: QuickDefaults;
  onClose: () => void;
  onSubmitted?: (tempId: string) => void;
  variant?: "inline" | "modal";
  closeOnSubmit?: boolean;
}) {
  const state = useSyncState();
  const prefs = usePrefs();
  const t = useT();
  const [text, setText] = useState("");
  const [desc, setDesc] = useState("");
  const [pickedDue, setPickedDue] = useState<DueChoice | undefined>(undefined);
  const [priority, setPriority] = useState<number | null>(null);
  const [labels, setLabels] = useState<string[]>(defaults.labels ?? []);
  const [target, setTarget] = useState<ProjectTarget>({
    projectId: defaults.projectId ?? state.user?.inbox_project_id ?? "",
    sectionId: defaults.sectionId ?? null,
  });
  const dateA = useAnchor();
  const prioA = useAnchor();
  const labelA = useAnchor();
  const projA = useAnchor();

  const ctx = useMemo(() => quickAddContext(state), [state]);
  const parsed = useMemo(
    () => parseForHighlight(text, { ...ctx, defaultProjectId: target.projectId }),
    [text, ctx, target.projectId],
  );

  const submit = () => {
    const engine = useApp.getState().engine;
    if (!engine || !parsed.content.trim()) return;
    const r = { ...parsed, priority: parsed.priority ?? priority };
    const due = pickedDue === undefined ? (defaults.due ?? null) : pickedDue && "date" in pickedDue ? pickedDue : null;
    const { commands, itemTempId } = quickAddCommands(
      r,
      desc,
      {
        ...defaults,
        projectId: target.projectId,
        sectionId: target.sectionId,
        due,
        labels,
      },
      state,
    );
    if (!parsed.due && pickedDue && "string" in pickedDue) {
      const add = commands.find((c) => c.type === "item_add");
      if (add) add.args.due = pickedDue;
    }
    if (!parsed.due && pickedDue === null) {
      const add = commands.find((c) => c.type === "item_add");
      if (add) delete add.args.due;
    }
    void engine.enqueue(commands);
    onSubmitted?.(itemTempId);
    setText("");
    setDesc("");
    if (closeOnSubmit) onClose();
  };

  const project = state.projects[parsed.project?.id ?? target.projectId];
  const section = target.sectionId ? state.sections[target.sectionId] : null;
  const effectiveDue: Due | null = parsed.due?.due
    ? parsed.due.due
    : pickedDue && "date" in pickedDue
      ? { date: pickedDue.date, string: pickedDue.date, lang: "en", is_recurring: false, timezone: null }
      : pickedDue === undefined && defaults.due
        ? { date: defaults.due.date, string: "", lang: "en", is_recurring: false, timezone: null }
        : null;
  const dueText = effectiveDue ? dueLabel(effectiveDue, prefs) : null;
  const shownPriority = parsed.priority ?? priority;

  return (
    <div
      className={variant === "inline" ? "editor-card" : "quick-add"}
      data-testid={variant === "inline" ? "inline-editor" : "quick-add"}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          submit();
        }
      }}
    >
      <HighlightInput
        value={text}
        onChange={setText}
        placeholder={variant === "modal" ? t("quickAddPlaceholder") : t("taskName")}
        onEnter={submit}
        className="hl-title"
        autoFocus
        testId="task-content-input"
      />
      <textarea
        className="desc-input"
        rows={1}
        placeholder={t("description")}
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
      />
      <div className="editor-chips">
        <button
          type="button"
          className={`btn btn-chip${dueText ? " active" : ""}`}
          onClick={dateA.open}
          data-testid="editor-date"
        >
          <span className={dueText ? `tone-${dueText.tone}` : ""} style={{ display: "inline-flex" }}>
            <IconCalendar size={16} />
          </span>
          <span className={dueText ? `tone-${dueText.tone}` : ""}>{dueText ? dueText.text : t("dueDate")}</span>
        </button>
        <button type="button" className={`btn btn-chip${shownPriority ? " active" : ""}`} onClick={prioA.open}>
          <span style={{ color: shownPriority ? PRIORITY_COLORS[shownPriority] : undefined, display: "inline-flex" }}>
            <IconFlag size={16} filled={Boolean(shownPriority && shownPriority > 1)} />
          </span>
          {shownPriority ? `P${5 - shownPriority}` : t("priority")}
        </button>
        <button type="button" className={`btn btn-chip${labels.length ? " active" : ""}`} onClick={labelA.open}>
          <IconLabel size={16} />
          {labels.length ? labels.join(", ") : t("labels")}
        </button>
      </div>
      <div className="editor-footer">
        <button type="button" className="btn btn-ghost btn-sm" onClick={projA.open} data-testid="editor-project">
          {project?.inbox_project ? (
            <IconInbox size={16} />
          ) : (
            <span style={{ color: colorHex(project?.color), display: "inline-flex" }}>
              <IconHash size={16} />
            </span>
          )}
          {parsed.project ? parsed.project.name : project?.inbox_project ? t("inbox") : (project?.name ?? t("inbox"))}
          {!parsed.project && section ? ` / ${section.name}` : ""}
          <IconChevronDown size={14} />
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t("cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!parsed.content.trim()}
            onClick={submit}
            data-testid="submit-task"
          >
            {t("addTask")}
          </button>
        </div>
      </div>
      {dateA.anchor && (
        <DatePicker anchor={dateA.anchor} onClose={dateA.close} value={effectiveDue} onChange={setPickedDue} />
      )}
      {prioA.anchor && (
        <PriorityMenu anchor={prioA.anchor} onClose={prioA.close} value={shownPriority ?? 1} onChange={setPriority} />
      )}
      {labelA.anchor && (
        <LabelPicker anchor={labelA.anchor} onClose={labelA.close} value={labels} onChange={setLabels} />
      )}
      {projA.anchor && (
        <ProjectPicker anchor={projA.anchor} onClose={projA.close} value={target} onChange={setTarget} />
      )}
    </div>
  );
}
